if (typeof ComplianceFaq !== 'function') {

  const complianceFaqSlideUp = (target, duration) => {
    target.style.transitionProperty = 'height, margin, padding';
    target.style.transitionDuration = duration + 'ms';
    target.style.boxSizing = 'border-box';
    target.style.height = target.offsetHeight + 'px';
    target.offsetHeight;
    target.style.overflow = 'hidden';
    target.style.height = '0';
    target.style.paddingTop = '0';
    target.style.paddingBottom = '0';
    target.style.marginTop = '0';
    target.style.marginBottom = '0';

    window.setTimeout(() => {
      target.hidden = true;
      target.style.display = 'none';
      target.style.removeProperty('height');
      target.style.removeProperty('padding-top');
      target.style.removeProperty('padding-bottom');
      target.style.removeProperty('margin-top');
      target.style.removeProperty('margin-bottom');
      target.style.removeProperty('overflow');
      target.style.removeProperty('transition-duration');
      target.style.removeProperty('transition-property');
    }, duration);
  };

  const complianceFaqSlideDown = (target, duration) => {
    target.hidden = false;
    target.style.removeProperty('display');

    let display = window.getComputedStyle(target).display;
    if (display === 'none') display = 'block';

    target.style.display = display;
    const height = target.offsetHeight;
    target.style.overflow = 'hidden';
    target.style.height = '0';
    target.style.paddingTop = '0';
    target.style.paddingBottom = '0';
    target.style.marginTop = '0';
    target.style.marginBottom = '0';
    target.offsetHeight;
    target.style.boxSizing = 'border-box';
    target.style.transitionProperty = 'height, margin, padding';
    target.style.transitionDuration = duration + 'ms';
    target.style.height = height + 'px';
    target.style.removeProperty('padding-top');
    target.style.removeProperty('padding-bottom');
    target.style.removeProperty('margin-top');
    target.style.removeProperty('margin-bottom');

    window.setTimeout(() => {
      target.style.removeProperty('height');
      target.style.removeProperty('overflow');
      target.style.removeProperty('transition-duration');
      target.style.removeProperty('transition-property');
    }, duration);
  };

  class ComplianceFaq extends HTMLElement {

    constructor() {
      super();
      this.allTopics = [];
      this.panels = [];
    }

    connectedCallback() {
      this.allTopics = this.readTopics();
      this.panels = [...this.querySelectorAll('[data-category-panel]')];

      this.addEventListener('click', (event) => {
        const categoryButton = event.target.closest('[data-category-trigger]');
        if (categoryButton && this.contains(categoryButton)) {
          this.selectCategory(categoryButton.dataset.categoryTrigger, { skipPaginationSync: true });
          this.scrollToCategory(categoryButton.dataset.categoryTrigger);
          return;
        }

        const topicButton = event.target.closest('[data-topic-trigger]');
        if (topicButton && this.contains(topicButton)) {
          const panel = topicButton.closest('[data-category-panel]');
          if (panel) this.selectTopic(panel, topicButton.dataset.topicTrigger);
          return;
        }

        const dropdownTrigger = event.target.closest('[data-topic-dropdown-trigger]');
        if (dropdownTrigger && this.contains(dropdownTrigger)) {
          this.toggleTopicDropdown(dropdownTrigger.closest('[data-topic-dropdown]'));
          return;
        }

        const dropdownOption = event.target.closest('[data-topic-option]');
        if (dropdownOption && this.contains(dropdownOption)) {
          const panel = dropdownOption.closest('[data-category-panel]');
          if (panel) {
            this.selectTopic(panel, dropdownOption.dataset.topicOption);
            this.closeTopicDropdown(dropdownOption.closest('[data-topic-dropdown]'));
          }
          return;
        }

        const accordionTrigger = event.target.closest('.compliance-faq-item__trigger');
        if (accordionTrigger && this.contains(accordionTrigger)) {
          const accordion = accordionTrigger.closest('[data-compliance-accordion]');
          this.toggleItem(accordionTrigger.closest('.compliance-faq-item'), accordion);
        }
      });

      this.panels.forEach((panel) => {
        try {
          this.hydratePanel(panel);
        } catch (error) {
          console.error('Compliance FAQ panel hydrate failed', error);
        }

        const select = panel.querySelector('[data-topic-select]');
        if (select && !select.dataset.bound) {
          select.dataset.bound = 'true';
          select.addEventListener('change', () => this.selectTopic(panel, select.value));
        }

        this.syncTopicDropdown(panel);
      });

      if (!this._topicDropdownOutsideBound) {
        this._topicDropdownOutsideBound = true;
        document.addEventListener('click', (event) => {
          if (!event.target.closest('[data-topic-dropdown]')) {
            this.closeAllTopicDropdowns();
          }
        });
      }

      const active = this.querySelector('[data-category-trigger].is-active');
      if (active) {
        this.selectCategory(active.dataset.categoryTrigger);
      }

      this.bindCategorySlider();
      this.setContactLink();
    }

    setContactLink() {
      const link = this.querySelector('[data-compliance-contact-link]');
      if (!link) return;

      const url = new URL(link.getAttribute('href'), window.location.origin);
      const partnerId = new URLSearchParams(window.location.search).get('partner')
        || (typeof getAssociatedPartnerId === 'function' ? getAssociatedPartnerId() : '');

      if (!partnerId) return;

      url.searchParams.set('partner', partnerId);
      link.href = `${url.pathname}${url.search}`;
    }

    isCategorySliderRtl() {
      return document.documentElement.getAttribute('dir') === 'rtl';
    }

    #getRtlMaxNativeScroll(holder) {
      const start = holder.scrollLeft;
      const maxScroll = Math.max(0, holder.scrollWidth - holder.clientWidth);

      holder.scrollLeft = start - maxScroll - 200;
      const endScroll = holder.scrollLeft;
      holder.scrollLeft = start;

      return endScroll;
    }

    #getCategoryStartAlignScrollLeft(holder, item) {
      const holderRect = holder.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();

      if (this.isCategorySliderRtl()) {
        return holder.scrollLeft + (itemRect.right - holderRect.right);
      }

      return holder.scrollLeft + (itemRect.left - holderRect.left);
    }

    #resolveCategoryScrollTarget(holder, items, itemIndex, options = {}) {
      const item = items[itemIndex];
      if (!item) return 0;

      const positions = options.positions || this.getCategoryScrollPositions(holder, items);
      const startScroll = positions[0] ?? 0;
      const endScroll = positions[positions.length - 1] ?? startScroll;
      const minBound = Math.min(startScroll, endScroll);
      const maxBound = Math.max(startScroll, endScroll);

      if (options.preferCached) {
        return positions[itemIndex] ?? 0;
      }

      const startTarget = this.#getCategoryStartAlignScrollLeft(holder, item);

      if (startTarget >= minBound && startTarget <= maxBound) {
        return startTarget;
      }

      return positions[itemIndex] ?? Math.max(minBound, Math.min(maxBound, endScroll));
    }

    #spreadTailScrollPositions(positions, endScroll) {
      positions[positions.length - 1] = endScroll;

      let tailStart = positions.length - 1;
      while (tailStart > 0 && positions[tailStart - 1] === positions[tailStart]) {
        tailStart--;
      }

      if (tailStart >= positions.length - 1) return positions;

      const startScroll = tailStart > 0 ? positions[tailStart - 1] : positions[0];
      const tailCount = positions.length - tailStart;

      for (let i = 0; i < tailCount; i++) {
        positions[tailStart + i] = i === tailCount - 1
          ? endScroll
          : startScroll + ((endScroll - startScroll) * (i + 1)) / (tailCount + 1);
      }

      return positions;
    }

    buildCategoryScrollPositions(holder, items) {
      const maxScroll = Math.max(0, holder.scrollWidth - holder.clientWidth);
      if (!items?.length) return [0];

      if (this.isCategorySliderRtl()) {
        const startScroll = holder.scrollLeft;
        const endScroll = this.#getRtlMaxNativeScroll(holder);
        const minBound = Math.min(startScroll, endScroll);
        const maxBound = Math.max(startScroll, endScroll);
        const positions = items.map((item) => {
          const startTarget = this.#getCategoryStartAlignScrollLeft(holder, item);

          if (startTarget >= minBound && startTarget <= maxBound) {
            return startTarget;
          }

          return endScroll;
        });

        positions[0] = startScroll;
        return this.#spreadTailScrollPositions(positions, endScroll);
      }

      const positions = items.map((item) => Math.min(item.offsetLeft, maxScroll));
      return this.#spreadTailScrollPositions(positions, maxScroll);
    }

    getCategoryScrollPositions(holder, items) {
      if (this._categoryScrollPositions?.length === items?.length) {
        return this._categoryScrollPositions;
      }

      return this.buildCategoryScrollPositions(holder, items);
    }

    getCategoryScrollLeftForHolder(holder, items, itemIndex, options = {}) {
      const positions = this.getCategoryScrollPositions(holder, items);

      if (this.isCategorySliderRtl()) {
        return this.#resolveCategoryScrollTarget(holder, items, itemIndex, {
          ...options,
          positions
        });
      }

      return positions[itemIndex] ?? 0;
    }

    getNearestCategoryIndex(holder, items, scrollValue) {
      const positions = this.getCategoryScrollPositions(holder, items);

      if (positions.length <= 1) return 0;

      const decreasing = positions[positions.length - 1] < positions[0];

      for (let index = 0; index < positions.length - 1; index++) {
        const midpoint = (positions[index] + positions[index + 1]) / 2;

        if (decreasing) {
          if (scrollValue >= midpoint) return index;
        } else if (scrollValue <= midpoint) {
          return index;
        }
      }

      return positions.length - 1;
    }

    getCategoryItemIndex(holder, items) {
      if (!holder || !items.length) return 0;
      return this.getNearestCategoryIndex(holder, items, holder.scrollLeft);
    }

    applyCategorySlideState(holder, items, itemIndex) {
      this._categoryActiveIndex = itemIndex;
      this._categoryScrollStartIndex = itemIndex;
      this.setCategoryPaginationProgress(itemIndex, items.length);

      const trigger = items[itemIndex]?.querySelector('[data-category-trigger]');
      if (trigger && !trigger.classList.contains('is-active')) {
        this.selectCategory(trigger.dataset.categoryTrigger);
      }
    }

    #completeCategoryScroll(holder, items, itemIndex) {
      const finish = () => {
        this._categorySliderBlock = false;
        this._categoryLockedIndex = null;
        this._categoryScrollPreferCached = false;
        this.applyCategorySlideState(holder, items, itemIndex);
      };

      const item = items[itemIndex];
      const targetScroll = this.getCategoryScrollLeftForHolder(holder, items, itemIndex, {
        preferCached: Boolean(this._categoryScrollPreferCached)
      });
      const needsScroll = Math.abs(holder.scrollLeft - targetScroll) > 3
        || !this.#isCategoryVisuallyAligned(holder, items, itemIndex);

      if (!needsScroll) {
        finish();
        return;
      }

      holder.scrollTo({ left: targetScroll, behavior: 'auto' });

      if (!this.isCategorySliderRtl()) {
        finish();
        return;
      }

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (!this.#isCategoryVisuallyAligned(holder, items, itemIndex)) {
            holder.scrollLeft = this.getCategoryScrollLeftForHolder(holder, items, itemIndex);
          }

          finish();
        });
      });
    }

    #scheduleCategoryScrollFinish(holder, items, itemIndex) {
      window.clearTimeout(this._categoryScrollFinishTimeout);

      let completed = false;
      const finishScroll = () => {
        if (completed) return;
        completed = true;
        window.clearTimeout(this._categoryScrollFinishTimeout);
        this.#completeCategoryScroll(holder, items, itemIndex);
      };

      if ('onscrollend' in holder) {
        holder.addEventListener('scrollend', finishScroll, { once: true });
      }

      this._categoryScrollFinishTimeout = window.setTimeout(
        finishScroll,
        this.isCategorySliderRtl() ? 550 : 450
      );
    }

    #scrollToCategoryIndex(holder, items, itemIndex, behavior = 'smooth', options = {}) {
      this._categorySliderBlock = true;
      this._categoryLockedIndex = itemIndex;

      holder.scrollTo({
        left: this.getCategoryScrollLeftForHolder(holder, items, itemIndex, options),
        behavior
      });
    }

    #isCategoryVisuallyAligned(holder, items, itemIndex) {
      const item = items[itemIndex];
      if (!item) return true;

      const alignmentThreshold = 2;
      const holderRect = holder.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();

      if (this.isCategorySliderRtl()) {
        const startAligned = Math.abs(itemRect.right - holderRect.right) <= alignmentThreshold;
        const endAligned = Math.abs(itemRect.left - holderRect.left) <= alignmentThreshold;
        return startAligned || endAligned;
      }

      return Math.abs(itemRect.left - holderRect.left) <= alignmentThreshold;
    }

    #resolveCategorySwipeIndex(holder, items) {
      const nearestIndex = this.getCategoryItemIndex(holder, items);
      const startIndex = this._categoryScrollStartIndex ?? this._categoryActiveIndex ?? 0;

      if (nearestIndex === startIndex) return nearestIndex;
      if (nearestIndex > startIndex) return Math.min(startIndex + 1, items.length - 1);

      return Math.max(startIndex - 1, 0);
    }

    #beginCategoryScrollGesture(holder, items) {
      if (this._categorySliderBlock || this._categoryLockedIndex != null) return;

      this._categoryScrollStartIndex = this._categoryActiveIndex ?? this.getCategoryItemIndex(holder, items);
    }

    #finishUserCategorySnap(holder, items, itemIndex) {
      window.clearTimeout(this._categoryUserSnapTimeout);
      this.applyCategorySlideState(holder, items, itemIndex);
    }

    #scheduleUserCategorySnapFinish(holder, items, itemIndex) {
      window.clearTimeout(this._categoryUserSnapTimeout);

      const finishSnap = () => {
        window.clearTimeout(this._categoryUserSnapTimeout);
        this.#finishUserCategorySnap(holder, items, itemIndex);
      };

      if ('onscrollend' in holder) {
        holder.addEventListener('scrollend', finishSnap, { once: true });
      }

      this._categoryUserSnapTimeout = window.setTimeout(finishSnap, 400);
    }

    snapToNearestCategoryPosition(holder, items) {
      const itemIndex = this.#resolveCategorySwipeIndex(holder, items);
      const targetScroll = this.getCategoryScrollLeftForHolder(holder, items, itemIndex);
      const snapThreshold = 8;

      if (Math.abs(holder.scrollLeft - targetScroll) <= snapThreshold) {
        this.applyCategorySlideState(holder, items, itemIndex);
        return itemIndex;
      }

      holder.scrollTo({ left: targetScroll, behavior: 'smooth' });
      this.#scheduleUserCategorySnapFinish(holder, items, itemIndex);
      return itemIndex;
    }

    #scheduleCategoryScrollSnap(holder, items) {
      window.clearTimeout(this._categoryScrollTimeout);
      window.clearTimeout(this._categoryScrollSettleTimeout);

      const snapWhenSettled = () => {
        if (this._categorySliderBlock || this._categoryLockedIndex != null) return;

        const currentScroll = holder.scrollLeft;

        if (this._categoryLastScrollLeft != null && Math.abs(currentScroll - this._categoryLastScrollLeft) > 1) {
          this._categoryLastScrollLeft = currentScroll;
          this._categoryScrollSettleTimeout = window.setTimeout(snapWhenSettled, 60);
          return;
        }

        this._categoryLastScrollLeft = currentScroll;
        this.snapToNearestCategoryPosition(holder, items);
      };

      this._categoryLastScrollLeft = holder.scrollLeft;
      this._categoryScrollTimeout = window.setTimeout(snapWhenSettled, 100);
    }

    scrollCategoryToItem(holder, item, itemIndex, items) {
      this.setCategoryPaginationProgress(itemIndex, items.length);
      this._categoryScrollPreferCached = true;
      this.#scrollToCategoryIndex(holder, items, itemIndex, 'smooth', { preferCached: true });
      this.#scheduleCategoryScrollFinish(holder, items, itemIndex);
    }

    setCategoryPaginationProgress(itemIndex, itemCount) {
      const paginationEl = this.querySelector('.compliance-faq__category-pagination');
      if (!paginationEl || !itemCount) return;

      const clampedIndex = Math.min(Math.max(itemIndex, 0), itemCount - 1);
      paginationEl.style.setProperty('--category-pagination-progress', clampedIndex + 1);
      this.syncCategoryFadeOverlay(clampedIndex, itemCount);
    }

    syncCategoryFadeOverlay(itemIndex, itemCount) {
      const mobileCategories = this.querySelector('.compliance-faq__categories--mobile');
      if (!mobileCategories || !itemCount) return;

      const isLastSlide = itemIndex >= itemCount - 1;
      mobileCategories.classList.toggle('is-scroll-end', isLastSlide);
    }

    finishCategoryScroll(holder, itemIndex) {
      const items = [...this.querySelectorAll('.js-compliance-category')];
      if (itemIndex != null && items.length) {
        this.#completeCategoryScroll(holder, items, itemIndex);
        return;
      }

      this._categorySliderBlock = false;
      this.syncCategorySliderDots();
    }

    bindCategorySlider() {
      const slider = this.querySelector('.compliance-faq__categories--mobile css-slider');
      const paginationEl = this.querySelector('.compliance-faq__category-pagination');
      if (!slider || !paginationEl) return;

      const setup = () => {
        window.requestAnimationFrame(() => {
          const holder = slider.querySelector('.css-slider-holder');
          const items = [...slider.querySelectorAll('.js-compliance-category')];

          this._categoryScrollPositions = holder && items.length
            ? this.buildCategoryScrollPositions(holder, items)
            : [0];

          const maxScroll = Math.max(0, holder.scrollWidth - holder.clientWidth);

          if (!holder || items.length <= 1 || maxScroll <= 8) {
            paginationEl.hidden = true;
            paginationEl.replaceChildren();
            this._categoryPaginationDots = [];
            this._categoryPaginationFill = null;
            paginationEl.style.removeProperty('--category-pagination-count');
            paginationEl.style.removeProperty('--category-pagination-progress');
            return;
          }

          paginationEl.replaceChildren();
          paginationEl.style.setProperty('--category-pagination-count', items.length);

          const fill = document.createElement('span');
          fill.className = 'compliance-faq__category-pagination-fill';
          fill.setAttribute('aria-hidden', 'true');
          paginationEl.appendChild(fill);
          this._categoryPaginationFill = fill;

          items.forEach((item, itemIndex) => {
            const segment = document.createElement('span');
            segment.className = 'compliance-faq__category-pagination-segment';
            segment.dataset.index = itemIndex;
            segment.addEventListener('click', () => {
              this.setCategoryPaginationProgress(itemIndex, items.length);

              const trigger = item.querySelector('[data-category-trigger]');
              if (trigger) this.selectCategory(trigger.dataset.categoryTrigger, { skipPaginationSync: true });

              this.scrollCategoryToItem(holder, item, itemIndex, items);
            });
            paginationEl.appendChild(segment);
          });

          paginationEl.hidden = false;
          this._categoryPaginationDots = paginationEl.querySelectorAll('.compliance-faq__category-pagination-segment');

          if (this._categoryScrollHandler) {
            holder.removeEventListener('scroll', this._categoryScrollHandler);
          }

          holder.classList.add('force-disable-snapping');

          this._categoryScrollHandler = () => {
            if (this._categorySliderBlock || this._categoryLockedIndex != null) return;

            if (!('onscrollend' in holder)) {
              this.#scheduleCategoryScrollSnap(holder, items);
            }
          };

          this._categoryScrollGestureHandler = () => {
            this.#beginCategoryScrollGesture(holder, items);
          };

          if (this._categoryScrollEndHandler) {
            holder.removeEventListener('scrollend', this._categoryScrollEndHandler);
          }

          if (this._categoryScrollGestureHandler) {
            holder.removeEventListener('touchstart', this._categoryScrollGestureHandler);
            holder.removeEventListener('mousedown', this._categoryScrollGestureHandler);
          }

          this._categoryScrollEndHandler = () => {
            if (this._categorySliderBlock || this._categoryLockedIndex != null) return;

            window.clearTimeout(this._categoryScrollTimeout);
            window.clearTimeout(this._categoryScrollSettleTimeout);
            this.snapToNearestCategoryPosition(holder, items);
          };

          holder.addEventListener('scroll', this._categoryScrollHandler, { passive: true });
          holder.addEventListener('touchstart', this._categoryScrollGestureHandler, { passive: true });
          holder.addEventListener('mousedown', this._categoryScrollGestureHandler);

          if ('onscrollend' in holder) {
            holder.addEventListener('scrollend', this._categoryScrollEndHandler, { passive: true });
          }

          this._categoryActiveIndex = this.getCategoryItemIndex(holder, items);
          this._categoryScrollStartIndex = this._categoryActiveIndex;
          this.syncCategorySliderDots();
        });
      };

      if (slider.classList.contains('enabled')) {
        setup();
      } else {
        slider.addEventListener('ready', setup, { once: true });
      }

      slider.addEventListener('reset', setup);
      window.addEventListener('resize', setup);
    }

    readTopics() {
      const node = this.querySelector('[id^="ComplianceFaqTopics-"]');
      if (!node) return [];
      try { return JSON.parse(node.textContent); } catch (e) { return []; }
    }

    normalizeId(value) {
      return String(value || '').split('/').pop();
    }

    idsMatch(left, right) {
      if (left == null || right == null || left === '' || right === '') return false;

      const leftId = this.normalizeId(left);
      const rightId = this.normalizeId(right);
      return leftId === rightId || String(left) === String(right) || String(left).includes(rightId) || String(right).includes(leftId);
    }

    findByDataValue(container, attr, value) {
      if (!container || value == null || value === '') return null;

      const dataKey = attr.replace(/-([a-z])/g, (_, char) => char.toUpperCase());
      return [...container.querySelectorAll('[data-' + attr + ']')].find((node) => this.idsMatch(node.dataset[dataKey], value)) || null;
    }

    normalizeName(value) {
      return (value || '')
        .replace(/\u00a0/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    decodeJson(source) {
      if (!source || source === 'null' || source === '[]') return null;

      const attempts = [
        source,
        source.replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
      ];

      for (const attempt of attempts) {
        try {
          return JSON.parse(attempt);
        } catch (e) {
          continue;
        }
      }

      return null;
    }

    readPanelTopicsJson(panel) {
      const node = panel.querySelector('script[type="application/json"][data-panel-topics]');
      if (node) {
        try { return JSON.parse(node.textContent); } catch (e) { /* fall through */ }
      }

      return this.decodeJson(panel.dataset.topicJson);
    }

    matchTopicId(name) {
      const normalized = this.normalizeName(name);
      if (!normalized) return null;

      const matched = this.allTopics.find((topic) => this.normalizeName(topic.name) === normalized);
      return matched ? matched.id : null;
    }

    parsePanelTopics(panel) {
      const parsed = this.readPanelTopicsJson(panel);
      if (!Array.isArray(parsed) || !parsed.length) return [];

      return parsed
        .map((entry, index) => {
          if (!entry || typeof entry !== 'object') return null;

          const name = this.normalizeName(entry.name);
          if (!name) return null;

          const matchedTopic = this.allTopics.find((topic) => {
            return this.normalizeName(topic.name) === name
              || this.idsMatch(topic.id, entry.id)
              || (entry.handle && topic.handle === entry.handle);
          });
          const matchedId = matchedTopic?.id || this.matchTopicId(name);
          const id = matchedId || entry.id || ('topic-' + index);
          const questionIds = (Array.isArray(entry.questions) && entry.questions.length
            ? entry.questions
            : matchedTopic?.questions || []).filter(Boolean);

          return {
            id: id,
            name: name,
            questionIds: questionIds,
            sortOrder: Number(entry.sort_order != null ? entry.sort_order : index)
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.sortOrder - b.sortOrder);
    }

    findQuestionNode(questionId) {
      if (!questionId) return null;

      const pool = this.querySelector('.compliance-faq__question-pool');
      if (!pool) return null;

      const exact = this.findByDataValue(pool, 'question-id', questionId);
      if (exact) return exact;

      return [...pool.querySelectorAll('[data-question-id]')].find((node) => {
        return this.idsMatch(node.dataset.questionId, questionId);
      }) || null;
    }

    findTopicPoolSource(topicId, topicName) {
      const pool = this.querySelector('.compliance-faq__pool');
      if (!pool) return null;

      const byId = this.findByDataValue(pool, 'topic-panel', topicId);
      if (byId && byId.innerHTML.trim()) return byId;

      const normalized = this.normalizeName(topicName);
      return [...pool.querySelectorAll('[data-topic-panel]')].find((node) => {
        return this.normalizeName(node.dataset.topicName) === normalized && node.innerHTML.trim();
      }) || null;
    }

    buildTopicPanelContent(topic) {
      const poolSource = this.findTopicPoolSource(topic.id, topic.name);
      if (poolSource) {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = poolSource.innerHTML;
        return wrapper;
      }

      const accordion = document.createElement('div');
      accordion.setAttribute('data-compliance-accordion', '');

      topic.questionIds.forEach((questionId) => {
        const source = this.findQuestionNode(questionId);
        if (!source) return;
        accordion.appendChild(source.cloneNode(true));
      });

      return accordion;
    }

    hydratePanel(panel) {
      const topics = this.parsePanelTopics(panel);
      if (!topics.length) {
        this.syncTopicsMobileState(panel);
        return;
      }

      const hasTopics = panel.querySelector('[data-topic-trigger]');
      const list = panel.querySelector('.compliance-faq__topic-list');
      const select = panel.querySelector('[data-topic-select]');
      const faqs = panel.querySelector('.compliance-faq__faqs');
      if (!list || !faqs) return;

      if (hasTopics) {
        topics.forEach((topic) => {
          const topicPanel = this.findByDataValue(panel, 'topic-panel', topic.id)
            || [...panel.querySelectorAll('[data-topic-panel]')].find((node) => {
              return this.normalizeName(node.dataset.topicName) === this.normalizeName(topic.name);
            });

          if (!topicPanel) return;

          const poolContent = this.buildTopicPanelContent(topic);
          const poolItems = poolContent.querySelectorAll('.compliance-faq-item').length;
          const serverItems = topicPanel.querySelectorAll('.compliance-faq-item').length;
          if (!poolItems) return;
          if (serverItems >= poolItems) return;

          topicPanel.innerHTML = '';
          topicPanel.appendChild(poolContent);
        });
        this.syncTopicsMobileState(panel);
        return;
      }

      list.innerHTML = '';
      faqs.innerHTML = '';
      if (select) select.innerHTML = '';

      topics.forEach((topic, index) => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'compliance-faq__topic-button' + (index === 0 ? ' is-active' : '');
        button.dataset.topicTrigger = topic.id;
        button.setAttribute('aria-current', index === 0 ? 'true' : 'false');
        button.textContent = topic.name;
        item.appendChild(button);
        list.appendChild(item);

        if (select) {
          const option = document.createElement('option');
          option.value = topic.id;
          option.textContent = topic.name;
          if (index === 0) option.selected = true;
          select.appendChild(option);
        }

        const panelEl = document.createElement('div');
        panelEl.className = 'compliance-faq__topic-panel';
        panelEl.dataset.topicPanel = topic.id;
        panelEl.hidden = index !== 0;
        panelEl.appendChild(this.buildTopicPanelContent(topic));
        faqs.appendChild(panelEl);
      });

      this.syncTopicDropdown(panel);
      this.syncTopicsMobileState(panel);
    }

    syncTopicsMobileState(panel) {
      const dropdown = panel.querySelector('[data-topic-dropdown]');
      const faqs = panel.querySelector('.compliance-faq__faqs');
      const select = panel.querySelector('[data-topic-select]');
      const hasTopics = (select && select.options.length > 0)
        || panel.querySelector('[data-topic-trigger]');

      if (dropdown) dropdown.hidden = !hasTopics;
      if (faqs) faqs.hidden = !hasTopics;
    }

    syncTopicDropdown(panel) {
      const dropdown = panel.querySelector('[data-topic-dropdown]');
      const select = panel.querySelector('[data-topic-select]');
      if (!dropdown || !select) return;

      const list = dropdown.querySelector('[data-topic-dropdown-list]');
      const valueEl = dropdown.querySelector('[data-topic-dropdown-value]');
      if (!list || !valueEl) return;

      if (!select.options.length) {
        list.innerHTML = '';
        valueEl.textContent = this.dataset.selectTopicLabel || '';
        this.syncTopicsMobileState(panel);
        return;
      }

      list.innerHTML = '';

      [...select.options].forEach((option) => {
        if (!option.value) return;

        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'compliance-faq__topic-dropdown-option' + (option.selected ? ' is-selected' : '');
        button.dataset.topicOption = option.value;
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', option.selected ? 'true' : 'false');

        const label = document.createElement('span');
        label.textContent = option.textContent;
        button.appendChild(label);

        const check = document.createElement('span');
        check.className = 'compliance-faq__topic-dropdown-check';
        check.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8L6.5 11.5L13 5" stroke="#333333" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        button.appendChild(check);

        item.appendChild(button);
        list.appendChild(item);
      });

      const selected = select.options[select.selectedIndex];
      valueEl.textContent = selected?.textContent || this.dataset.selectTopicLabel || '';
      this.syncTopicsMobileState(panel);
    }

    updateTopicDropdownValue(dropdown, topicId) {
      if (!dropdown) return;

      const select = dropdown.querySelector('[data-topic-select]');
      const valueEl = dropdown.querySelector('[data-topic-dropdown-value]');
      if (select) select.value = topicId;

      dropdown.querySelectorAll('[data-topic-option]').forEach((option) => {
        const selected = option.dataset.topicOption === topicId;
        option.classList.toggle('is-selected', selected);
        option.setAttribute('aria-selected', selected ? 'true' : 'false');
      });

      const selectedOption = dropdown.querySelector('[data-topic-option].is-selected');
      if (valueEl) {
        valueEl.textContent = selectedOption?.firstElementChild?.textContent
          || selectedOption?.textContent
          || this.dataset.selectTopicLabel
          || '';
      }
    }

    toggleTopicDropdown(dropdown) {
      if (!dropdown) return;

      if (dropdown.classList.contains('is-open')) {
        this.closeTopicDropdown(dropdown);
        return;
      }

      this.closeAllTopicDropdowns();
      dropdown.classList.add('is-open');
      dropdown.querySelector('[data-topic-dropdown-trigger]')?.setAttribute('aria-expanded', 'true');
      dropdown.querySelector('[data-topic-dropdown-list]')?.removeAttribute('hidden');
    }

    closeTopicDropdown(dropdown) {
      if (!dropdown) return;

      dropdown.classList.remove('is-open');
      dropdown.querySelector('[data-topic-dropdown-trigger]')?.setAttribute('aria-expanded', 'false');
      dropdown.querySelector('[data-topic-dropdown-list]')?.setAttribute('hidden', '');
    }

    closeAllTopicDropdowns() {
      this.querySelectorAll('[data-topic-dropdown].is-open').forEach((dropdown) => {
        this.closeTopicDropdown(dropdown);
      });
    }

    selectCategory(categoryId, options = {}) {
      if (!categoryId) return;

      const categoryButtons = [...this.querySelectorAll('[data-category-trigger]')];

      categoryButtons.forEach((button) => {
        const active = button.dataset.categoryTrigger === categoryId;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      if (!options.skipPaginationSync) {
        this.syncCategorySliderDots();
      }

      this.panels.forEach((panel) => {
        const active = panel.dataset.categoryPanel === categoryId;
        panel.hidden = !active;
        if (active) {
          this.closeAllTopicDropdowns();
          const first = panel.querySelector('[data-topic-trigger]');
          if (first) {
            this.selectTopic(panel, first.dataset.topicTrigger);
          } else {
            this.syncTopicDropdown(panel);
            this.syncTopicsMobileState(panel);
          }
        }
      });
    }

    scrollToCategory(categoryId) {
      if (!categoryId) return;

      const holder = this.querySelector('.compliance-faq__categories--mobile .css-slider-holder');
      if (!holder) return;

      const slide = [...this.querySelectorAll('.js-compliance-category')].find((node) => {
        const trigger = node.querySelector('[data-category-trigger]');
        return trigger && trigger.dataset.categoryTrigger === categoryId;
      });

      if (!slide) return;

      const items = [...this.querySelectorAll('.js-compliance-category')];
      const slideIndex = items.indexOf(slide);

      this.setCategoryPaginationProgress(slideIndex, items.length);
      this.scrollCategoryToItem(holder, slide, slideIndex, items);
    }

    syncCategorySliderDots() {
      if (!this._categoryPaginationDots?.length) return;

      const holder = this.querySelector('.compliance-faq__categories--mobile .css-slider-holder');
      const items = [...this.querySelectorAll('.js-compliance-category')];
      if (!holder || !items.length) return;

      const itemIndex = this._categoryLockedIndex ?? this.getCategoryItemIndex(holder, items);
      this.setCategoryPaginationProgress(itemIndex, items.length);
    }

    selectTopic(panel, topicId) {
      if (!topicId) return;

      panel.querySelectorAll('[data-topic-trigger]').forEach((button) => {
        const active = this.idsMatch(button.dataset.topicTrigger, topicId);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-current', active ? 'true' : 'false');
      });

      const select = panel.querySelector('[data-topic-select]');
      if (select) select.value = topicId;

      const dropdown = panel.querySelector('[data-topic-dropdown]');
      if (dropdown) this.updateTopicDropdownValue(dropdown, topicId);

      panel.querySelectorAll('[data-topic-panel]').forEach((topicPanel) => {
        const active = this.idsMatch(topicPanel.dataset.topicPanel, topicId);
        topicPanel.hidden = !active;
        if (active) this.openFirst(topicPanel);
      });
    }

    toggleItem(item, accordion) {
      if (!item) return;
      const isOpen = item.classList.contains('is-open');
      accordion.querySelectorAll('.compliance-faq-item.is-open').forEach((open) => this.setItem(open, false));
      if (!isOpen) this.setItem(item, true);
    }

    openFirst(topicPanel) {
      topicPanel.querySelectorAll('.compliance-faq-item').forEach((item, index) => {
        this.setItem(item, index === 0);
      });
    }

    setItem(item, open) {
      const trigger = item.querySelector('.compliance-faq-item__trigger');
      const content = item.querySelector('.compliance-faq-item__content');
      if (!trigger || !content) return;

      const isOpen = item.classList.contains('is-open');
      if (open === isOpen) return;

      item.classList.toggle('is-open', open);
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');

      if (open) {
        complianceFaqSlideDown(content, 200);
        return;
      }

      if (content.hidden || window.getComputedStyle(content).display === 'none') {
        content.hidden = true;
        return;
      }

      complianceFaqSlideUp(content, 200);
    }
  }

  if (typeof customElements.get('compliance-faq') === 'undefined') {
    customElements.define('compliance-faq', ComplianceFaq);
  }
}
